import { ReactNode } from 'react';

import { FullProject, FullTask, LoggedUser, TaskWithSub } from 'entities';

import { IconProps } from 'components/Icons';

import { TaskModal, useProjectProfile, ProjectProfileContext } from '../context';
import { useUser } from 'hooks';
import { listGet } from 'utils/array';

import { GetLabelFn } from 'types';

type TaskRef = {
  task: TaskWithSub & Partial<FullTask>;
  updateTask: (ref: Parameters<ProjectProfileContext['updateTask']>[1]) => void;
};

type OptionHookResult = {
  getLabel?: GetLabelFn;

  loading?: boolean;

  isEnabled: boolean;

  action: () => void | Promise<void>;
};

export type UseTaskOption = (taskId: string) => OptionHookResult & TaskRef;

type CreatorParams = {
  loggedUser: LoggedUser;
  openModal: (modal: TaskModal) => void;
  project: FullProject;
  task: TaskWithSub & Partial<FullTask>;
  updateTask: (ref: Parameters<ProjectProfileContext['updateTask']>[1]) => void;
};

type UseHookCreator = (params: CreatorParams) => OptionHookResult;

function createTaskOptionHook(creator: UseHookCreator): UseTaskOption {
  return (taskId: string) => {
    const loggedUser = useUser();
    const {
      project,
      taskModalConfig: { openModal },
      updateTask,
    } = useProjectProfile();

    const taskData: TaskRef = {
      task: listGet(project.tasks, taskId)!,
      updateTask: (ref) => updateTask(taskId, ref),
    };

    return {
      ...taskData,
      ...creator({
        loggedUser,
        openModal: (modal) => openModal(modal, taskId),
        project,
        ...taskData,
      }),
    };
  };
}

export type TaskOptionOverlayProps = TaskRef & {
  isOpen: boolean;
  close: () => void;
};

export type TaskAction =
  | 'CHANGE_OWNER'
  | 'CANCEL_TASK'
  | 'REQUEST_DATE_CHANGE'
  | 'CHANGE_OWNER'
  | 'CHANGE_SUB_OWNER'
  | 'REMOVE_SUB_OWNER';

type TaskOptionCommonParams = {
  getLabel?: GetLabelFn;

  Icon: ((props: IconProps) => JSX.Element) | ReactNode;

  useOption: UseHookCreator;

  /**
   * This will only work when the task option menu is open,
   * which is why it can be a single key with no ctrl/shift etc
   */
  shortcutKey?: string;
};

type OptionTypeParams =
  | {
      modal: TaskModal;

      overlay: (props: TaskOptionOverlayProps) => JSX.Element | null;

      action?: never;
    }
  | { modal?: never; overlay?: never; action: TaskAction };

export type TaskOptionParams = TaskOptionCommonParams & OptionTypeParams;

export function createTaskOption({ useOption, ...params }: TaskOptionParams) {
  return {
    useTaskAction: createTaskOptionHook(useOption),
    ...params,
  };
}

export type TaskOptionConfig = ReturnType<typeof createTaskOption>;

export type TaskOption = TaskAction | TaskModal;
